import { CardDetailsRequest, CardType } from '../ApiModel';
import { field, validate } from '../helper/ValidationHelper';
import { is } from '../helper/ValidationHelper';
import FormStore from './FormStore';

type CardDetailsFormValues = {
    cardholder: string;
    card_type: CardType | '';
    number: string;
    cvv: string;
    expiry_month: string;
    expiry_year: string;
};

// Matches the domain's CreditCard.Name rule, which mirrors the payment gateway's minimum.
const minimumCardholderLength = 3;

function cardIsExpired(values: CardDetailsFormValues) {
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const selectedYear = parseInt(values.expiry_year, 10);
    const selectedMonth = parseInt(values.expiry_month, 10);
    return selectedYear < currentYear || (selectedYear === currentYear && selectedMonth < currentMonth);
}

function validateCardDetails(values: CardDetailsFormValues) {
    const errors = validate(
        values,
        field(
            'cardholder',
            is.requiredNotEmpty('Cardholder name is required'),
            is.invalidIf(
                (c) => c.trim() !== '' && c.trim().length < minimumCardholderLength,
                `Cardholder name must be at least ${minimumCardholderLength} characters`
            )
        ),
        field(
            'card_type',
            is.required('Card type is required'),
            is.invalidIf(() => values.card_type === '', 'Card type is required')
        ),
        field(
            'number',
            is.required('Card number is required'),
            is.validIf((n) => /^\d{13,19}$/.test(n.replace(/\s/g, '')), 'Please enter a valid card number')
        ),
        field(
            'cvv',
            is.required('CVV is required'),
            is.validIf((c) => /^\d{3,4}$/.test(c), 'Please enter a valid CVV (3-4 digits)')
        ),
        field(
            'expiry_month',
            is.required('Expiry month is required'),
            is.minLength(1, 'Expiry month is required'),
            is.invalidIf(() => cardIsExpired(values), 'Card cannot be expired')
        ),
        field('expiry_year', is.required('Expiry year is required'), is.minLength(4, 'Expiry year is required'))
    );
    return errors;
}

export default class CardDetailsFormStore extends FormStore<CardDetailsFormValues> {
    static newDefaultFormValues = () =>
        ({
            cardholder: '',
            card_type: '',
            number: '',
            cvv: '',
            expiry_month: '',
            expiry_year: '',
        }) as CardDetailsFormValues;

    constructor() {
        super(CardDetailsFormStore.newDefaultFormValues());
    }

    cardIsExpired() {
        return cardIsExpired(this.values);
    }

    validate(): boolean {
        return super.validate(validateCardDetails);
    }

    asCardDetailsRequest(): CardDetailsRequest {
        return {
            cardholder: this.values.cardholder.trim(),
            card_type: this.values.card_type as CardType,
            number: this.values.number.replace(/\s/g, ''),
            cvv: this.values.cvv,
            expiry_month: parseInt(this.values.expiry_month, 10),
            expiry_year: parseInt(this.values.expiry_year, 10),
        };
    }

    init() {
        super.init(CardDetailsFormStore.newDefaultFormValues());
    }
}
